import { crossSubdomainStorage } from "@/integrations/supabase/crossSubdomainStorage";

/**
 * The staff org selection: which organization a DigaVouch staff member is
 * viewing, when it is not their own.
 *
 * ## Who decides, and where
 *
 * Nothing here grants anything. The backend decides, on EVERY request, whether
 * the caller may act in the selected org (platform staff within the
 * environment's scope; everyone else only their own org). This module only
 * remembers the selection and puts it on the wire as `X-DigaVouch-Org`. A
 * selection the server refuses comes back as 403 `ORG_SELECTION_FORBIDDEN`,
 * and `request()` in `src/lib/api.ts` clears it on that code. The server never
 * substitutes an org for the one the client asked for, so what the banner
 * names is what the data is.
 *
 * ## Why a cookie on `.digavouch.com`, not localStorage
 *
 * `ProtectedRoute` moves an operator to their org's host (`feather.uat...`),
 * and the selection has to survive that full navigation, exactly like the
 * sign-in session does. `crossSubdomainStorage` is the store that already
 * solves this for the session (a cookie scoped to the parent domain on
 * `*.digavouch.com`, localStorage elsewhere), so the selection rides the same
 * mechanism. It holds one org id and nothing else.
 *
 * ## Reactive
 *
 * `useOperatorMe` keys its query on the selection, so a change refetches the
 * org, and with it the vertical, the subdomain and the flow picker. Readers
 * subscribe through `useStaffOrgSelection()` (`src/hooks/useOperatorMe.ts`).
 */

/** The request header the backend reads. */
export const ORG_SELECTION_HEADER = "X-DigaVouch-Org";

export const STAFF_ORG_STORAGE_KEY = "dv-staff-org";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface SimpleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

let storage: SimpleStorage = crossSubdomainStorage;
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** The selected org id, or null when the person is viewing their own org. */
export function getStaffOrgSelection(): string | null {
  try {
    const raw = storage.getItem(STAFF_ORG_STORAGE_KEY);
    return raw && UUID.test(raw) ? raw.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Remember a selection (`null` = back to the person's own org). */
export function setStaffOrgSelection(orgId: string | null): void {
  try {
    if (orgId && UUID.test(orgId)) storage.setItem(STAFF_ORG_STORAGE_KEY, orgId.toLowerCase());
    else storage.removeItem(STAFF_ORG_STORAGE_KEY);
  } catch {
    // Storage can be unavailable (privacy mode); the selection then lives
    // for this page only, and the server still decides every request.
  }
  notify();
}

export function clearStaffOrgSelection(): void {
  setStaffOrgSelection(null);
}

export function subscribeStaffOrgSelection(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The header to send, or nothing: an operator with no selection sends no header at all. */
export function staffOrgHeaders(): Record<string, string> {
  const selection = getStaffOrgSelection();
  return selection ? { [ORG_SELECTION_HEADER]: selection } : {};
}

/** Test-only: swap the backing store. */
export function _setStaffOrgStorageForTest(next: SimpleStorage | null): void {
  storage = next ?? crossSubdomainStorage;
}
